<template>
    <div class="sales-dashboard">
        <div class="container">
            <div class="header">
                <h1>🎯 销售业绩大屏</h1>
                <div class="header-right">
                    <el-select v-model="selectedYear" @change="onYearChange" size="small" style="width: 120px; margin-right: 15px;">
                        <el-option label="实时" value="current" />
                        <el-option label="上一年度" value="last" />
                    </el-select>
                    <div class="time" id="time">{{ currentTime }}</div>
                </div>
            </div>

            <div class="card kpi-card" style="grid-column: span 2;">
                <div class="kpi-label">年度总销售额</div>
                <div class="kpi-value">{{ totalSales }}</div>
            </div>

            <div class="card kpi-card" style="grid-column: span 2;">
                <div class="kpi-label">{{ monthSalesLabel }}</div>
                <div class="kpi-value">{{ monthSales }}</div>
            </div>

            <div class="card kpi-card">
                <div class="kpi-label">本年激活代理商</div>
                <div class="kpi-value">{{ agentYearCount }}</div>
            </div>

            <div class="card kpi-card">
                <div class="kpi-label">本月激活代理商</div>
                <div class="kpi-value">{{ agentMonthCount }}</div>
            </div>

            <div class="card" style="grid-column: span 2;">
                <div class="card-title">📊 年度销售数字</div>
                <div id="rankChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 2;">
                <div class="card-title">📈 季度同比</div>
                <div id="quarterChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 2;">
                <div class="card-title">📈 区域销售占比</div>
                <div id="regionChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 3;">
                <div class="card-title">📊 月度销售趋势</div>
                <div id="monthlyChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 3;">
                <div class="card-title">{{ partnerChartTitle }}</div>
                <div id="partnerChart" class="chart"></div>
            </div>

            <div class="card" style="grid-column: span 6;">
                <div class="card-title">📊 本年每月销售额及增长率</div>
                <div id="monthlyTotalChart" class="chart"></div>
            </div>

            <div class="card report-card" style="grid-column: span 6;">
                <div class="card-title">
                    📋 销售明细报表
                    <el-button-group style="margin-left: 15px;">
                        <el-button size="small" :type="viewMode === 'table' ? 'primary' : ''" @click="viewMode = 'table'">
                            <el-icon><List /></el-icon> 表格
                        </el-button>
                        <el-button size="small" :type="viewMode === 'pivot' ? 'primary' : ''" @click="viewMode = 'pivot'">
                            <el-icon><Grid /></el-icon> 透视
                        </el-button>
                    </el-button-group>
                </div>
                <div class="report-container" v-show="viewMode === 'table'">
                    <div class="report-filters">
                        <div class="filter-group">
                            <label>时间段:</label>
                            <el-date-picker v-model="reportStartMonth" type="month" placeholder="开始" format="YYYY-MM" value-format="YYYYMM" class="dark-picker" />
                            <span class="separator">至</span>
                            <el-date-picker v-model="reportEndMonth" type="month" placeholder="结束" format="YYYY-MM" value-format="YYYYMM" class="dark-picker" />
                        </div>
                        <div class="filter-group">
                            <label>筛选:</label>
                            <el-select v-model="filterRegionType" placeholder="类型" clearable class="dark-select" @change="loadReportData">
                                <el-option label="GLB" value="GLB" />
                                <el-option label="CN" value="CN" />
                            </el-select>
                            <el-select v-model="filterSalesRegion" placeholder="区域" clearable class="dark-select" @change="loadReportData">
                                <el-option label="南区" value="南区" />
                                <el-option label="北区" value="北区" />
                                <el-option label="西区" value="西区" />
                                <el-option label="东区" value="东区" />
                            </el-select>
                            <el-select v-model="filterSalesName" placeholder="销售" clearable filterable class="dark-select" @change="loadReportData">
                                <el-option v-for="item in salesOptions" :key="item" :label="item" :value="item" />
                            </el-select>
                            <el-select v-model="filterAgentId" placeholder="代理" clearable filterable class="dark-select" @change="loadReportData">
                                <el-option v-for="item in agentOptions" :key="item" :label="item" :value="item" />
                            </el-select>
                        </div>
                        <div class="filter-actions">
                            <el-button type="primary" @click="loadReportData" :loading="reportLoading" class="glow-btn">
                                <el-icon><Search /></el-icon> 查询
                            </el-button>
                            <el-button type="success" @click="exportReport" :disabled="!reportData.length" class="glow-btn">
                                <el-icon><Download /></el-icon> 导出
                            </el-button>
                        </div>
                    </div>
                    <div class="data-table">
                        <div class="table-header">
                            <div class="th th-id">代理ID</div>
                            <div class="th th-name">代理名称</div>
                            <div class="th th-region">区域</div>
                            <div class="th th-sales">销售</div>
                            <div class="th th-type">类型</div>
                            <div class="th th-amount">金额(USD)</div>
                            <div class="th th-ratio">占比</div>
                        </div>
                        <div class="table-body">
                            <div v-if="filteredReportData.length === 0" class="empty-state">
                                <el-icon class="empty-icon"><DocumentDelete /></el-icon>
                                <p>暂无数据，请选择时间段并查询</p>
                            </div>
                            <div v-for="(row, idx) in filteredReportData" :key="idx" class="tr" @click="selectRow(row)" :class="{selected: selectedRow === row}">
                                <div class="td td-id">{{ row.agent_id }}</div>
                                <div class="td td-name" :title="row.agent_name">{{ row.agent_name }}</div>
                                <div class="td td-region">
                                    <span class="region-badge" :class="'region-' + row.sales_region">{{ row.sales_region }}</span>
                                </div>
                                <div class="td td-sales">{{ row.sales_name }}</div>
                                <div class="td td-type">
                                    <span class="type-badge" :class="row.region_type === 'CN' ? 'type-cn' : 'type-glb'">{{ row.region_type }}</span>
                                </div>
                                <div class="td td-amount amount">${{ row.sales_amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }}</div>
                                <div class="td td-ratio">
                                    <div class="progress-bar">
                                        <div class="progress-fill" :style="{width: (row.sales_amount / maxAmount * 100) + '%'}"></div>
                                        <span class="progress-text">{{ (row.sales_amount / totalAmount * 100).toFixed(1) }}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="pivot-container" v-show="viewMode === 'pivot'">
                    <div v-if="reportLoading" class="pivot-hint">
                        <el-icon class="hint-icon loading"><Loading /></el-icon>
                        <p>正在加载数据...</p>
                    </div>
                    <PivotTable v-else :data="filteredReportData" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import {ajaxGet} from '@/api/request';
import PivotTable from './PivotTable.vue';

export default {
    name: 'salesDashboard',
    components: {
        PivotTable
    },
    data() {
        return {
            currentTime: '',
            totalSales: '$0',
            monthSales: '$0',
            monthSalesLabel: '本月销售额',
            agentYearCount: 0,
            agentMonthCount: 0,
            charts: {},
            reportStartMonth: '',
            reportEndMonth: '',
            reportData: [],
            reportLoading: false,
            filterAgentId: '',
            filterSalesRegion: '',
            filterSalesName: '',
            filterRegionType: '',
            selectedRow: null,
            viewMode: 'table',
            autoLoadedForPivot: false,
            selectedYear: 'current',
            partnerChartTitle: '🤝 本月合作伙伴TOP10'
        }
    },
    mounted() {
        // 代理商和客户用户自动跳转到对应Dashboard
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userInfo.type === 2) {
            this.$router.replace('/agentDashboard');
            return;
        }
        if (userInfo.type === 4) {
            this.$router.replace('/customerDashboard');
            return;
        }
        
        // 每年前两个月默认显示上一年度
        const now = new Date();
        if (now.getMonth() < 2) {
            this.selectedYear = 'last';
        }
        
        this.updateTime();
        setInterval(this.updateTime, 1000);
        this.initCharts();
        this.loadData();
        this.initReportDates();
        window.addEventListener('resize', this.resizeCharts);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.resizeCharts);
        Object.values(this.charts).forEach(chart => {
            if (chart) {
                chart.clear();
                chart.dispose();
            }
        });
    },
    methods: {
        updateTime() {
            this.currentTime = new Date().toLocaleString('zh-CN');
        },
        initReportDates() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            this.reportStartMonth = `${year}${month}`;
            this.reportEndMonth = `${year}${month}`;
        },
        async autoLoadDataForPivot() {
            if (!this.reportData.length && !this.autoLoadedForPivot && this.reportStartMonth && this.reportEndMonth) {
                this.autoLoadedForPivot = true;
                await this.loadReportData();
            }
        },
        async loadReportData() {
            if (!this.reportStartMonth || !this.reportEndMonth) {
                this.$message.warning('请选择时间段');
                return;
            }
            this.reportLoading = true;
            try {
                const response = await ajaxGet({
                    url: '/stat/sales_report/',
                    params: {
                        start_month: this.reportStartMonth,
                        end_month: this.reportEndMonth
                    }
                });
                this.reportData = response.data || response || [];
                this.selectedRow = null;
            } catch (error) {
                console.error('Error loading report:', error);
                this.$message.error('加载报表失败');
            } finally {
                this.reportLoading = false;
            }
        },
        selectRow(row) {
            this.selectedRow = this.selectedRow === row ? null : row;
        },
        exportReport() {
            if (!this.reportStartMonth || !this.reportEndMonth) {
                this.$message.warning('请选择时间段');
                return;
            }
            const url = `/api/stat/sales_report/export/?start_month=${this.reportStartMonth}&end_month=${this.reportEndMonth}`;
            window.open(url, '_blank');
        },
        initCharts() {
            this.$nextTick(() => {
                Object.keys(this.charts).forEach(key => {
                    if (this.charts[key]) {
                        this.charts[key].dispose();
                    }
                });
                this.charts = {};
                
                this.charts.monthlyChart = echarts.init(document.getElementById('monthlyChart'));
                this.charts.rankChart = echarts.init(document.getElementById('rankChart'));
                this.charts.quarterChart = echarts.init(document.getElementById('quarterChart'));
                this.charts.regionChart = echarts.init(document.getElementById('regionChart'));
                this.charts.partnerChart = echarts.init(document.getElementById('partnerChart'));
                this.charts.monthlyTotalChart = echarts.init(document.getElementById('monthlyTotalChart'));
            });
        },
        resizeCharts() {
            Object.values(this.charts).forEach(chart => chart && chart.resize());
        },
        onYearChange() {
            this.loadData();
            // 刷新报表数据
            if (this.reportStartMonth && this.reportEndMonth) {
                this.loadReportData();
            }
        },
        async loadData() {
            const params = { year_mode: this.selectedYear };
            try {
                const [totalData, currentMonthData, agentStats, quarterData, monthlyData, regionData, partnerData, monthlyTotalData] = await Promise.all([
                    ajaxGet({url: '/stat/dashboard/total_sales/', params}),
                    ajaxGet({url: '/stat/dashboard/monthly_sales_current/', params}),
                    ajaxGet({url: '/stat/dashboard/agent_stats/', params}),
                    ajaxGet({url: '/stat/dashboard/quarterly_compare/', params}),
                    ajaxGet({url: '/stat/dashboard/monthly_sales/', params}),
                    ajaxGet({url: '/stat/dashboard/region_sales/', params}),
                    ajaxGet({url: '/stat/dashboard/partner_sales/', params}),
                    ajaxGet({url: '/stat/dashboard/monthly_total_sales/', params})
                ]);

                const totalSalesValue = totalData.total_sales || totalData.data?.total_sales || 0;
                this.totalSales = '$' + (totalSalesValue / 1000000).toFixed(2) + 'M';
                this.agentYearCount = agentStats.year || agentStats.data?.year || 0;
                this.agentMonthCount = agentStats.month || agentStats.data?.month || 0;

                const monthlyData_data = monthlyData.data || monthlyData || [];
                this.renderRankChart(monthlyData_data);
                const quarterData_data = quarterData.data || quarterData || [];
                this.renderQuarterChart(quarterData_data);
                this.renderMonthlyChart(monthlyData_data);
                const regionData_data = regionData.data || regionData || [];
                this.renderRegionChart(regionData_data);
                const partnerData_data = partnerData.data || partnerData || [];
                const partnerMonth = partnerData.month || null;
                if (partnerMonth) {
                    const py = partnerMonth.slice(0, 4);
                    const pm = partnerMonth.slice(4, 6);
                    const nowMonth = new Date().toISOString().slice(0,7).replace('-','');
                    this.partnerChartTitle = partnerMonth !== nowMonth ? `🤝 ${py}年${pm}月合作伙伴TOP10` : '🤝 本月合作伙伴TOP10';
                }
                this.renderPartnerChart(partnerData_data);
                const monthlyTotalData_data = monthlyTotalData.data || monthlyTotalData || [];
                this.renderMonthlyTotalChart(monthlyTotalData_data);

                // 本月销售额：直接用 monthly_sales_current 接口数据求和
                const currentMonthData_data = currentMonthData.data || currentMonthData || [];
                const actualMonth = currentMonthData.month || null;
                const monthSales = currentMonthData_data.reduce((sum, d) => sum + (d.sales_amount || 0), 0);
                if (actualMonth && actualMonth !== new Date().toISOString().slice(0,7).replace('-','')) {
                    const y = actualMonth.slice(0, 4);
                    const m = actualMonth.slice(4, 6);
                    this.monthSalesLabel = `${y}年${m}月销售额`;
                } else {
                    this.monthSalesLabel = '本月销售额';
                }
                this.monthSales = '$' + (monthSales / 1000000).toFixed(2) + 'M';
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            }
        },
        renderRankChart(data) {
            if (!data || data.length === 0) return;
            if (!this.charts.rankChart) {
                this.charts.rankChart = echarts.init(document.getElementById('rankChart'));
            }
            this.charts.rankChart.clear();
            
            // 按销售人员汇总年度数据
            const salesMap = {};
            data.forEach(d => {
                const sales = d.sales || '';
                if (!salesMap[sales]) {
                    salesMap[sales] = 0;
                }
                salesMap[sales] += (d.sales_amount || 0);
            });
            
            // 转换为数组并排序
            const salesArray = Object.entries(salesMap)
                .map(([sales, amount]) => ({ sales, sales_amount: amount }))
                .sort((a, b) => b.sales_amount - a.sales_amount)
                .slice(0, 10);
            
            const option = {
                grid: { left: 60, right: 30, top: 15, bottom: 60 },
                xAxis: { 
                    type: 'category', 
                    data: salesArray.map(d => d.sales),
                    axisLabel: { color: '#fff', fontSize: 12, rotate: 30, interval: 0 },
                    axisLine: { lineStyle: { color: '#00f2fe' } }
                },
                yAxis: { 
                    type: 'value',
                    axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' },
                    splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } }
                },
                tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                    borderColor: '#00f2fe', 
                    borderWidth: 1,
                    formatter: params => {
                        const p = params[0];
                        return p.name + '<br/>年度销售额: $' + (p.value / 1000000).toFixed(2) + 'M';
                    }
                },
                series: [{
                    type: 'bar',
                    data: salesArray.map(d => d.sales_amount),
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
                            { offset: 0, color: '#f093fb' },
                            { offset: 0.5, color: '#f5576c' },
                            { offset: 1, color: '#ff6b6b' }
                        ])
                    },
                    label: { show: true, position: 'top', color: '#fff', fontSize: 10, formatter: p => '$' + (p.value / 1000000).toFixed(1) + 'M' },
                    barWidth: '60%'
                }]
            };
            this.charts.rankChart.setOption(option);
        },
        renderQuarterChart(data) {
            if (!data || data.length === 0) return;
            if (!this.charts.quarterChart) {
                this.charts.quarterChart = echarts.init(document.getElementById('quarterChart'));
            }
            this.charts.quarterChart.clear();
            const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
            const currentYear = new Date().getFullYear().toString();
            const lastYear = (currentYear - 1).toString();
            const currentYearData = quarters.map(q => {
                const item = data.find(d => String(d.year) === currentYear && d.quarter === q);
                return item ? (item.sales_amount || 0) : 0;
            });
            const lastYearData = quarters.map(q => {
                const item = data.find(d => String(d.year) === lastYear && d.quarter === q);
                return item ? (item.sales_amount || 0) : 0;
            });
            const growthRate = currentYearData.map((val, idx) => 
                lastYearData[idx] > 0 ? ((val - lastYearData[idx]) / lastYearData[idx] * 100).toFixed(1) : 0
            );

            const option = {
                color: ['#f093fb', '#00f2fe', '#ffd700'],
                grid: { left: 60, right: 60, top: 45, bottom: 30 },
                legend: { data: [lastYear, currentYear, '同比增长'], textStyle: { color: '#00f2fe', fontSize: 12 }, top: 10 },
                xAxis: { type: 'category', data: quarters, axisLabel: { color: '#fff', fontSize: 12 }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                yAxis: [
                    { type: 'value', name: '销售额', nameTextStyle: { color: '#00f2fe' }, axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                    { type: 'value', name: '增长率', nameTextStyle: { color: '#ffd700' }, axisLabel: { color: '#ffd700', formatter: '{value}%' }, splitLine: { show: false } }
                ],
                tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                    borderColor: '#00f2fe', 
                    borderWidth: 1,
                    formatter: params => {
                        let result = params[0].name + '<br/>';
                        params.forEach(p => {
                            if (p.seriesName === '同比增长') {
                                result += p.seriesName + ': ' + p.value + '%<br/>';
                            } else {
                                result += p.seriesName + ': $' + (p.value / 1000000).toFixed(2) + 'M<br/>';
                            }
                        });
                        return result;
                    }
                },
                series: [
                    { name: lastYear, type: 'bar', data: lastYearData, barWidth: '30%', label: { show: true, position: 'top', color: '#fff', fontSize: 10, formatter: p => p.value > 0 ? '$' + (p.value / 1000000).toFixed(1) + 'M' : '' } },
                    { name: currentYear, type: 'bar', data: currentYearData, barWidth: '30%', label: { show: true, position: 'top', color: '#fff', fontSize: 10, formatter: p => p.value > 0 ? '$' + (p.value / 1000000).toFixed(1) + 'M' : '' } },
                    { name: '同比增长', type: 'line', yAxisIndex: 1, data: growthRate, itemStyle: { color: '#ffd700' }, lineStyle: { width: 3 }, symbol: 'circle', symbolSize: 8, label: { show: true, formatter: p => p.value != 0 ? p.value + '%' : '', color: '#ffd700', fontSize: 10 } }
                ]
            };
            this.charts.quarterChart.setOption(option);
        },
        renderMonthlyChart(data) {
            if (!data || data.length === 0) return;
            if (!this.charts.monthlyChart) {
                this.charts.monthlyChart = echarts.init(document.getElementById('monthlyChart'));
            }
            this.charts.monthlyChart.clear();
            const months = [...new Set(data.map(d => d.month))].sort();
            const salesPeople = [...new Set(data.map(d => d.sales))].filter(s => s);
            const colors = ['#00f2fe', '#4facfe', '#f093fb', '#f5576c', '#00ff88', '#ffd700', '#ff6b6b', '#4ecdc4'];

            const option = {
                color: colors,
                legend: { data: salesPeople, textStyle: { color: '#00f2fe', fontSize: 12 }, top: 10, type: 'scroll' },
                grid: { left: 60, right: 30, top: 45, bottom: 30 },
                xAxis: { type: 'category', data: months.map(m => m + '月'), axisLabel: { color: '#00f2fe', fontSize: 11, interval: 0 }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                yAxis: { type: 'value', axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                    borderColor: '#00f2fe', 
                    borderWidth: 1,
                    formatter: params => {
                        let result = params[0].axisValue + '<br/>';
                        params.forEach(p => {
                            result += p.marker + p.seriesName + ': $' + (p.value / 1000000).toFixed(2) + 'M<br/>';
                        });
                        return result;
                    }
                },
                series: salesPeople.map((person, idx) => ({
                    name: person,
                    type: 'line',
                    smooth: true,
                    symbol: 'circle',
                    symbolSize: 6,
                    data: months.map(m => {
                        const item = data.find(d => d.month === m && d.sales === person);
                        return item ? (item.sales_amount || 0) : 0;
                    })
                }))
            };
            this.charts.monthlyChart.setOption(option);
        },
        renderRegionChart(data) {
            if (!data || data.length === 0) return;
            if (!this.charts.regionChart) {
                this.charts.regionChart = echarts.init(document.getElementById('regionChart'));
            }
            this.charts.regionChart.clear();
            const option = {
                color: ['#00f2fe', '#4facfe', '#f093fb', '#f5576c'],
                tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe', borderWidth: 1 },
                series: [{
                    type: 'pie',
                    radius: ['40%', '70%'],
                    center: ['50%', '45%'],
                    data: data.filter(d => d.sales_amount > 0).map(d => ({ name: d.region, value: d.sales_amount })),
                    label: { color: '#fff', fontSize: 14, formatter: p => p.name + '\n' + p.percent + '%' },
                    itemStyle: { borderRadius: 10, borderColor: '#000', borderWidth: 3 }
                }]
            };
            this.charts.regionChart.setOption(option);
        },
        renderPartnerChart(data) {
            if (!data || data.length === 0) return;
            if (!this.charts.partnerChart) {
                this.charts.partnerChart = echarts.init(document.getElementById('partnerChart'));
            }
            this.charts.partnerChart.clear();
            const top10 = data.slice(0, 10);
            const option = {
                grid: { left: 150, right: 60, top: 5, bottom: 30 },
                xAxis: { type: 'value', axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M', fontSize: 11 }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                yAxis: { type: 'category', data: top10.map(d => d.agent_name || '').reverse(), axisLabel: { color: '#fff', fontSize: 11, interval: 0 }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                    borderColor: '#00f2fe', 
                    borderWidth: 1,
                    formatter: params => {
                        const p = params[0];
                        return p.name + '<br/>销售额: $' + (p.value / 1000000).toFixed(2) + 'M';
                    }
                },
                series: [{
                    type: 'bar',
                    data: top10.map(d => d.sales_amount || 0).reverse(),
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                            { offset: 0, color: '#00f2fe' },
                            { offset: 0.5, color: '#4facfe' },
                            { offset: 1, color: '#00ff88' }
                        ])
                    },
                    label: { show: true, position: 'right', color: '#fff', fontSize: 10, formatter: p => '$' + (p.value / 1000000).toFixed(1) + 'M' },
                    barWidth: '70%'
                }]
            };
            this.charts.partnerChart.setOption(option);
        },
        renderMonthlyTotalChart(data) {
            if (!data || data.length === 0) return;
            if (!this.charts.monthlyTotalChart) {
                this.charts.monthlyTotalChart = echarts.init(document.getElementById('monthlyTotalChart'));
            }
            this.charts.monthlyTotalChart.clear();
            const allMonths = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
            const salesData = allMonths.map(m => {
                const item = data.find(d => d.month === m);
                return item ? item.sales_amount : 0;
            });
            const growthRates = salesData.map((val, idx) => {
                if (idx === 0 || salesData[idx - 1] === 0) return 0;
                return ((val - salesData[idx - 1]) / salesData[idx - 1] * 100).toFixed(1);
            });

            const option = {
                color: ['#00f2fe', '#ffd700'],
                grid: { left: 60, right: 60, top: 40, bottom: 30 },
                legend: { data: ['月销售额', '环比增长率'], textStyle: { color: '#00f2fe' }, top: 0 },
                xAxis: { type: 'category', data: allMonths.map(m => m + '月'), axisLabel: { color: '#fff' }, axisLine: { lineStyle: { color: '#00f2fe' } } },
                yAxis: [
                    { type: 'value', name: '销售额', nameTextStyle: { color: '#00f2fe' }, axisLabel: { color: '#00f2fe', formatter: v => '$' + (v / 1000000).toFixed(1) + 'M' }, splitLine: { lineStyle: { color: 'rgba(0, 242, 254, 0.1)' } } },
                    { type: 'value', name: '增长率', nameTextStyle: { color: '#ffd700' }, axisLabel: { color: '#ffd700', formatter: '{value}%' }, splitLine: { show: false } }
                ],
                tooltip: { backgroundColor: 'rgba(0, 0, 0, 0.8)', borderColor: '#00f2fe', borderWidth: 1 },
                series: [
                    {
                        name: '月销售额',
                        type: 'bar',
                        data: salesData,
                        itemStyle: {
                            color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
                                { offset: 0, color: '#00f2fe' },
                                { offset: 0.5, color: '#4facfe' },
                                { offset: 1, color: '#00ff88' }
                            ])
                        },
                        label: { show: true, position: 'inside', color: '#fff', fontSize: 10, formatter: p => p.value > 0 ? '$' + (p.value / 1000000).toFixed(1) + 'M' : '' },
                        barWidth: '50%'
                    },
                    {
                        name: '环比增长率',
                        type: 'line',
                        yAxisIndex: 1,
                        data: growthRates,
                        itemStyle: { color: '#ffd700' },
                        lineStyle: { width: 3 },
                        symbol: 'circle',
                        symbolSize: 8,
                        label: { show: true, formatter: p => p.value != 0 ? p.value + '%' : '', color: '#ffd700', fontSize: 10, offset: [0, -10] }
                    }
                ]
            };
            this.charts.monthlyTotalChart.setOption(option);
        }
    },
    computed: {
        agentOptions() {
            return [...new Set(this.reportData.map(d => d.agent_id))].sort();
        },
        salesOptions() {
            return [...new Set(this.reportData.map(d => d.sales_name))].filter(s => s && s !== '未分配' && s !== 'test').sort();
        },
        filteredReportData() {
            const filtered = this.reportData.filter(row => {
                if (this.filterRegionType && row.region_type !== this.filterRegionType) return false;
                if (this.filterSalesRegion && row.sales_region !== this.filterSalesRegion) return false;
                if (this.filterSalesName && row.sales_name !== this.filterSalesName) return false;
                if (this.filterAgentId && row.agent_id !== this.filterAgentId) return false;
                return true;
            });
            
            // 按 agent_id, agent_name, sales_region, sales_name, region_type 汇总
            const aggregated = {};
            filtered.forEach(row => {
                const key = `${row.agent_id}_${row.sales_region}_${row.sales_name}_${row.region_type}`;
                if (!aggregated[key]) {
                    aggregated[key] = {
                        agent_id: row.agent_id,
                        agent_name: row.agent_name,
                        sales_region: row.sales_region,
                        sales_name: row.sales_name,
                        region_type: row.region_type,
                        sales_amount: 0
                    };
                }
                aggregated[key].sales_amount += row.sales_amount;
            });
            
            return Object.values(aggregated).sort((a, b) => b.sales_amount - a.sales_amount);
        },
        totalAmount() {
            return this.filteredReportData.reduce((sum, row) => sum + row.sales_amount, 0);
        },
        maxAmount() {
            return Math.max(...this.filteredReportData.map(row => row.sales_amount), 0);
        }
    },
    watch: {
        viewMode(newVal) {
            if (newVal === 'pivot') {
                this.autoLoadDataForPivot();
            }
        }
    }
}
</script>

<style scoped>
.sales-dashboard {
    background: linear-gradient(135deg, #000000, #0a0e27, #1a1a2e, #16213e);
    background-size: 400% 400%;
    animation: bgAnimation 15s ease infinite;
    height: calc(100vh - 120px);
    color: #fff;
    overflow-y: auto;
    overflow-x: hidden;
}

@keyframes bgAnimation {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
}

.container {
    width: 100%;
    padding: 10px;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: 50px 100px 280px 280px 300px;
    gap: 8px;
}

.header {
    grid-column: 1 / 7;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 30px;
    border: 2px solid rgba(0, 255, 255, 0.3);
}

.header-right {
    display: flex;
    align-items: center;
    gap: 15px;
}

.header h1 {
    font-size: 28px;
    background: linear-gradient(90deg, #00f2fe 0%, #4facfe 50%, #00f2fe 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: bgAnimation 3s linear infinite;
}

.time {
    font-size: 14px;
    color: #00f2fe;
}

.card {
    background: rgba(0, 0, 0, 0.6);
    border-radius: 10px;
    padding: 15px;
    border: 2px solid rgba(0, 255, 255, 0.3);
    transition: all 0.3s ease;
    overflow: hidden;
}

.card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 255, 255, 0.4);
}

.kpi-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.kpi-value {
    font-size: 32px;
    font-weight: bold;
    background: linear-gradient(90deg, #00f2fe 0%, #4facfe 50%, #f093fb 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: bgAnimation 3s linear infinite;
    margin: 8px 0;
}

.kpi-label {
    font-size: 12px;
    color: #00f2fe;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.chart {
    width: 100%;
    height: calc(100% - 35px);
}

.card-title {
    font-size: 14px;
    margin-bottom: 10px;
    color: #00f2fe;
    border-left: 3px solid #00f2fe;
    padding-left: 8px;
    line-height: 1.2;
}

.report-card {
    min-height: 450px;
}

.report-container {
    width: 100%;
    height: 100%;
}

.report-filters {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
    padding: 10px;
    background: rgba(0, 242, 254, 0.05);
    border-radius: 8px;
    margin-bottom: 15px;
}

.filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-group label {
    color: #00f2fe;
    font-size: 13px;
    white-space: nowrap;
}

.separator {
    color: #00f2fe;
    margin: 0 5px;
}

.filter-actions {
    display: flex;
    gap: 10px;
    margin-left: auto;
}

.dark-picker {
    width: 130px;
}

.dark-select {
    width: 110px;
}

.dark-picker :deep(.el-input__wrapper),
.dark-select :deep(.el-input__wrapper) {
    background: rgba(0, 242, 254, 0.1);
    border: 1px solid rgba(0, 242, 254, 0.3);
    box-shadow: none;
}

.dark-picker :deep(.el-input__inner),
.dark-select :deep(.el-input__inner) {
    color: #00f2fe;
}

.glow-btn {
    border: 1px solid rgba(0, 242, 254, 0.5);
    background: rgba(0, 242, 254, 0.1);
    color: #00f2fe;
    transition: all 0.3s;
}

.glow-btn:hover {
    background: rgba(0, 242, 254, 0.3);
    box-shadow: 0 0 15px rgba(0, 242, 254, 0.5);
}

.data-table {
    margin-top: 15px;
    border: 1px solid rgba(0, 242, 254, 0.2);
    border-radius: 8px;
    overflow-x: auto;
    overflow-y: hidden;
}

.table-header {
    display: flex;
    flex-direction: row;
    background: linear-gradient(90deg, rgba(0, 242, 254, 0.3), rgba(79, 172, 254, 0.3));
    padding: 12px 15px;
    font-weight: bold;
    color: #00f2fe;
    font-size: 13px;
    align-items: center;
    min-width: 970px;
}

.table-body {
    max-height: 350px;
    overflow-y: auto;
    overflow-x: hidden;
    min-width: 970px;
}

.table-body::-webkit-scrollbar {
    width: 6px;
}

.table-body::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
}

.table-body::-webkit-scrollbar-thumb {
    background: rgba(0, 242, 254, 0.3);
    border-radius: 3px;
}

.tr {
    display: flex;
    flex-direction: row;
    padding: 12px 15px;
    border-bottom: 1px solid rgba(0, 242, 254, 0.1);
    transition: all 0.3s;
    cursor: pointer;
    align-items: center;
    min-width: 970px;
}

.tr:hover {
    background: rgba(0, 242, 254, 0.15);
    box-shadow: inset 0 0 20px rgba(0, 242, 254, 0.1);
}

.tr.selected {
    background: rgba(0, 242, 254, 0.25);
    border-left: 3px solid #00f2fe;
}

.th, .td {
    padding: 0 10px;
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
}

.th {
    text-align: center;
    font-weight: bold;
}

.th-id, .td-id { width: 100px; flex: 0 0 100px; }
.th-name, .td-name { width: 250px; flex: 0 0 250px; }
.th-region, .td-region { width: 90px; flex: 0 0 90px; text-align: center; }
.th-sales, .td-sales { width: 90px; flex: 0 0 90px; text-align: center; }
.th-type, .td-type { width: 80px; flex: 0 0 80px; text-align: center; }
.th-amount, .td-amount { width: 160px; flex: 0 0 160px; text-align: right; }
.th-ratio, .td-ratio { width: 200px; flex: 0 0 200px; }

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: rgba(0, 242, 254, 0.5);
}

.empty-icon {
    font-size: 48px;
    margin-bottom: 15px;
}

.empty-state p {
    font-size: 14px;
    margin: 0;
}

.td {
    color: #fff;
}

.td.amount {
    color: #00ff88;
    font-weight: bold;
    font-size: 13px;
}

.region-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: bold;
}

.region-南区 { background: rgba(245, 87, 108, 0.3); color: #f5576c; border: 1px solid #f5576c; }
.region-北区 { background: rgba(0, 242, 254, 0.3); color: #00f2fe; border: 1px solid #00f2fe; }
.region-西区 { background: rgba(240, 147, 251, 0.3); color: #f093fb; border: 1px solid #f093fb; }
.region-东区 { background: rgba(255, 215, 0, 0.3); color: #ffd700; border: 1px solid #ffd700; }

.type-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: bold;
}

.type-cn { background: rgba(255, 107, 107, 0.3); color: #ff6b6b; border: 1px solid #ff6b6b; }
.type-glb { background: rgba(0, 255, 136, 0.3); color: #00ff88; border: 1px solid #00ff88; }

.progress-bar {
    position: relative;
    width: 100%;
    height: 20px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 10px;
    overflow: hidden;
}

.progress-fill {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    background: linear-gradient(90deg, #00f2fe, #4facfe, #00ff88);
    transition: width 0.5s ease;
    box-shadow: 0 0 10px rgba(0, 242, 254, 0.5);
}

.progress-text {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    font-size: 11px;
    font-weight: bold;
    z-index: 1;
    text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
}

.pivot-container {
    width: 100%;
    margin-top: 15px;
}

.card-title :deep(.el-button-group) {
    display: inline-flex;
}

.card-title :deep(.el-button) {
    border-color: rgba(0, 242, 254, 0.3);
    background: rgba(0, 242, 254, 0.1);
    color: #00f2fe;
}

.card-title :deep(.el-button--primary) {
    background: rgba(0, 242, 254, 0.3);
    border-color: #00f2fe;
}

.pivot-hint {
    text-align: center;
    padding: 60px 20px;
    color: rgba(255, 215, 0, 0.8);
}

.hint-icon {
    font-size: 48px;
    margin-bottom: 15px;
    color: #ffd700;
}

.pivot-hint p {
    font-size: 14px;
    margin: 0;
}

.hint-icon.loading {
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
</style>
